/**
 * The lesson screen for one student: units rail → Watch → Warm-up (review + count today's table) → Practice → Fact map.
 * All teaching rules live in src/engine; this file only draws and wires.
 */
import { SETTINGS, UNITS, ROUTES, routeStart, isDiv, keyShows, stagesFor, unitById, REVIEW_STAGE, STAGES_TO_UNLOCK_NEXT, PATTERN_STAGES, type Stage, type Unit } from "../engine/curriculum";
import { anchorCue, divCue, LANDED_ON, missHint, KEEP_GOING, CORRECT, NOT_YET } from "../engine/feedback";
import { puzzleFor, keyForFact } from "../engine/puzzle";
import { unitRecord, warmDoneToday, markWarmDone, recordAttempt, passStage, revisitIn, streak, type StudentProgress } from "../engine/progress";
import { buildWarmup, CountRun, type WarmRow, type CountRow, type FactRow } from "../engine/warmup";
import { buildWorksheet, buildReview, recycle, isRecycled, passed, type Worksheet } from "../engine/worksheet";
import type { Store, Student } from "../data/store";
import { keypadHTML, wireMarking, wireCounting, buildOrder, rich, esc, type Guide } from "./keypad";
import { factMapHTML, hasDivision } from "./factmap";

export interface LessonOptions {
  store: Store;
  student: Student;
  progress: StudentProgress;
  /** Teacher preview: every stage open, warm-up optional. */
  preview: boolean;
  onBack: () => void;
  /** Label for the back button (default "Switch student"). */
  backLabel?: string;
}

/** What a stage puts on the keypad for a problem. */
/** Even tables (8, 6, 4, 12) repeat their code: show the ten counts as two rows of five, so 8 sits above 48 and 24 above 64 (Meliesa, Oct 29). */
const countedTwice = (t: number): boolean => t % 2 === 0 && t !== 2 && t !== 10;

function guideForStage(st: Stage, a: number, b: number): Guide | undefined {
  const p = puzzleFor(a, b);
  if (st.guide === "full") return { arrows: "forward", puzzle: p };
  if (st.guide === "start") return { puzzle: p };
  return undefined;
}

export function mountLesson(root: HTMLElement, o: LessonOptions): () => void {
  const { store, student } = o;
  const P = o.progress;
  const $ = <T extends HTMLElement = HTMLElement>(id: string) => root.querySelector<T>(`#${id}`)!;
  const save = () => void store.saveProgress(student.id, P).catch((e) => console.warn("save failed", e));

  type View = { mode: "unit"; stage: number } | { mode: "review" };
  let view: View = { mode: "unit", stage: 0 };
  let sheet: (Worksheet & { built?: boolean }) | null = null;
  let warm: { rows: WarmRow[]; i: number; cleanup: (() => void) | null } | null = null;

  const unit = (): Unit => unitById(P.currentUnit);
  const stage = (): Stage => (view.mode === "review" ? REVIEW_STAGE : stagesFor(unit())[view.stage]!);
  const warmDone = (u: Unit) => o.preview || warmDoneToday(P, u.id);
  const unitLocked = (u: Unit) => {
    if (o.preview) return false;
    const i = UNITS.indexOf(u);
    if (i === 0) return false;
    const prev = UNITS[i - 1]!;
    return (P.units[prev.id]?.stage ?? 0) < STAGES_TO_UNLOCK_NEXT;
  };

  root.innerHTML = `
  <header class="band"><div class="wrap">
    <div><div class="brand">THE TIGARD METHOD<sup>™</sup></div><div class="tagline">Pattern · Logic · Discovery</div></div>
    <div class="who"><span>${esc(student.firstName)}</span><button type="button" id="back">${esc(o.backLabel ?? "Switch student")}</button></div>
  </div></header>
  <main><div class="wrap">
    <nav aria-label="Times-table units">
      <h2>Tigard Times Tables</h2>
      <div class="units" id="units"></div>
      <p class="streak" id="streak"></p>
      ${o.preview ? '<p class="note">Teacher preview: every stage open, warm-up optional.</p>' : ""}
    </nav>
    <section class="lesson">
      <div><div class="eyebrow" id="eyebrow"></div><h1 id="title"></h1></div>
      <div class="step" id="warmStep">
        <div class="steplabel"><span class="num" id="warmNum">1</span><h2 id="warmTitle">Warm-up</h2></div>
        <div class="card" id="warm" aria-live="polite"></div>
      </div>
      <div class="step">
        <div class="steplabel"><span class="num">2</span><h2 id="practiceTitle">Practice</h2></div>
        <div class="card"><div class="stages" id="stages" role="group" aria-label="Practice stages"></div><div id="practice" aria-live="polite"></div></div>
        <div class="row"><button class="btn ghost small" id="review" type="button">Mixed review</button><span class="note" style="margin:0">Every unit so far, no tools, plus facts to revisit.</span></div>
      </div>
      <div class="step">
        <div class="steplabel"><span class="num">3</span><h2>Fact map</h2></div>
        <div class="mapwrap"><table class="map" id="map" aria-label="Progress on every times fact"></table></div>
        <div id="divmapwrap" hidden><h3 style="margin:14px 0 6px">Division</h3><div class="mapwrap"><table class="map" id="divmap" aria-label="Progress on every division fact"></table></div></div>
        <div class="legend">
          <span><i></i>Not tried</span>
          <span><i style="background:var(--warn);border-color:var(--warn)"></i>Revisit</span>
          <span><i style="background:var(--sky);border-color:var(--navy)"></i>Correct</span>
          <span><i style="background:var(--navy);border-color:var(--navy)"></i>Known: no tools, quick</span>
        </div>
      </div>
    </section>
  </div></main>
  <footer><div class="wrap">The Tigard Method™ · © 2026 Meliesa Tigard</div></footer>`;

  /* ---------- Units rail ---------- */
  function renderUnits() {
    const box = $("units");
    box.innerHTML = "";
    for (const u of UNITS) {
      const st = stagesFor(u), us = unitRecord(P, u.id), locked = unitLocked(u);
      const label = locked ? "Locked" : us.stage >= st.length ? "Done ✓" : us.stage >= STAGES_TO_UNLOCK_NEXT ? `Stage ${us.stage + 1}/${st.length} · next open` : `Stage ${us.stage + 1}/${st.length}`;
      const b = document.createElement("button");
      b.type = "button";
      b.className = "unit";
      b.dataset.locked = String(locked);
      b.disabled = locked;
      b.setAttribute("aria-current", String(u.id === P.currentUnit && view.mode === "unit"));
      b.innerHTML = `<span>${esc(u.name)}</span><span class="pct">${label}</span>`;
      b.onclick = () => {
        P.currentUnit = u.id;
        view = { mode: "unit", stage: Math.min(us.stage, st.length - 1) };
        save();
        build();
      };
      box.appendChild(b);
    }
    const n = streak(P);
    $("streak").textContent = n > 0 ? `Warm-up streak: ${n} day${n === 1 ? "" : "s"}.` : "";
  }

  /* ---------- Warm-up ---------- */
  function renderWarm() {
    const u = unit(), box = $("warm");
    $("warmNum").classList.toggle("done", warmDoneToday(P, u.id));
    $("warmTitle").textContent = UNITS.indexOf(u) === 0 ? "Count forward and back" : isDiv(u) ? "Times facts review, then count today's table" : "Review, then count today's table";
    if (warmDoneToday(P, u.id)) {
      box.innerHTML = `<div class="done-line"><span class="check">✓ Warm-up done for today.</span><span class="muted">Practice is open.</span><button class="btn ghost small" id="again" type="button">Do it again</button></div>`;
      $("again").onclick = () => { warm = { rows: buildWarmup(u, P), i: 0, cleanup: null }; drawWarmRow(); };
      return;
    }
    if (!warm) warm = { rows: buildWarmup(u, P), i: 0, cleanup: null };
    if (warm.rows.length === 0) { markWarmDone(P, u.id); save(); renderWarm(); return; }
    drawWarmRow();
  }
  function warmHeader(introText: string): string {
    const pct = Math.round((100 * warm!.i) / warm!.rows.length);
    return `<p style="margin:0">${introText}${o.preview ? ' <span class="muted">(Preview: optional.)</span>' : ""}</p><div class="bar"><i style="width:${pct}%"></i></div>`;
  }
  const stepLine = () => `<p class="pmeta" style="margin:2px 0 10px">Step ${warm!.i + 1} of ${warm!.rows.length}</p>`;
  function drawWarmRow() {
    const row = warm!.rows[warm!.i]!;
    if (row.kind === "fact") return drawWarmFact(row);
    if (row.kind === "build") return drawBuild($("warm"), row.table, { review: true, stage: row.stage }, rowDone, warmHeader(`Review the ${row.table}s: draw your keypad.`) + stepLine());
    if (row.input === "typed") return drawTypedCount(row);
    drawTappedCount(row);
  }
  /** Today's table: tap the keys in order, with direction arrows. Back count: circle on 0, arrows reversed. */
  function drawTappedCount(row: CountRow) {
    const box = $("warm"), run = new CountRun(row), t = row.table, fwd = row.dir === "forward";
    const guide: Guide = { start: row.targets[0]! % 10, arrows: fwd ? "forward" : "back" };
    box.innerHTML = `${warmHeader("Count forward and back before you start the problems. Say each number out loud as you tap it.")}
      <h3>${fwd ? "Forward" : "Back"} by ${t}s${row.rounds > 1 ? ` <span class="muted" style="font-family:var(--body);font-weight:400;font-size:.9rem">· time ${row.round + 1} of ${row.rounds}</span>` : ""}</h3>${stepLine()}
      <div class="work"><div>
        <p style="margin-top:0">${fwd ? `Start at <b>${t}</b> and tap the keys in order up to <b>${t * SETTINGS.COUNT_TO}</b>. Follow the arrows.` : `Start at <b>${t * SETTINGS.COUNT_TO}</b>, on the <b>0</b>, and tap the keys in order back down to <b>${t}</b>. Follow the arrows.`}${row.fade ? " This time only the number you just counted stays on the screen." : ""} You can also type the digits.</p>
        <div class="countline" id="wline" aria-live="polite"></div><div class="fb" id="wfb"></div>
      </div>
      <div class="toolcol"><span class="lbl">Keypad</span><div id="wkp">${keypadHTML(t, "live", guide)}</div></div></div>`;
    const line = $("wline"), kp = $("wkp"), fb = $("wfb");
    if (countedTwice(t)) line.classList.add("twice");
    // The answer squares are on screen before the student counts: the current one is outlined, and fills as they tap.
    const cells: HTMLElement[] = row.targets.map((_, i) => { const s = document.createElement("span"); s.className = "cell" + (i === 0 ? " cur" : ""); s.setAttribute("aria-label", `number ${i + 1}`); line.appendChild(s); return s; });
    let lastBtn: HTMLElement | null = null;
    const btnFor = (d: number) => kp.querySelector<HTMLButtonElement>(`button.kc[data-d="${d}"]`);
    const tap = (d: number) => {
      if (run.done) return;
      const target = run.target!, prev = run.previous;
      const got = run.tap(d);
      if (got !== null) {
        lastBtn?.classList.remove("mark"); lastBtn?.querySelector(".ord")?.remove();
        const b = btnFor(d)!; b.classList.add("mark");
        const o2 = document.createElement("span"); o2.className = "ord"; o2.textContent = String(got); b.appendChild(o2); lastBtn = b;
        // The highlight moves on to the key to count next (Meliesa, Oct 29): after 63, the 6 is outlined for 56.
        kp.querySelector(".kc.cur")?.classList.remove("cur");
        if (!run.done) btnFor(run.target! % 10)?.classList.add("cur");
        const i = row.targets.indexOf(got);
        if (row.fade) cells.forEach((c, k) => { c.textContent = k === i ? String(got) : ""; c.className = "cell" + (k === i ? " ok" : k === i + 1 ? " cur" : ""); });
        else { cells[i]!.textContent = String(got); cells[i]!.className = "cell ok"; if (cells[i + 1]) cells[i + 1]!.className = "cell cur"; }
        fb.textContent = ""; fb.className = "fb";
        if (run.done) setTimeout(rowDone, 500);
      } else {
        fb.className = "fb warn";
        const hint = missHint(target, Math.floor(target / 10) * 10 + d, prev, fwd ? "forward" : "back", t);
        fb.textContent = `${NOT_YET} You're counting ${row.dir} by ${t}s${prev !== null ? ` from ${prev}` : ""}. ${hint || "Follow the arrow to the next key."}`;
      }
    };
    kp.querySelectorAll<HTMLButtonElement>("button.kc").forEach((b) => (b.onclick = () => { typed = ""; tap(Number(b.dataset.d)); }));
    // Typing: the digits build the whole number and show in the square as they're typed, so a typo can be seen and
    // backspaced. Nothing is judged until the number is complete (or Enter). A right start says "what does it end with?"
    let typed = "";
    const curCell = () => cells[row.targets.indexOf(run.target!)];
    const showTyped = () => { const cc = curCell(); if (cc) cc.textContent = typed; };
    const judge = () => {
      const target = String(run.target!);
      if (typed === target) { typed = ""; tap(Number(target.slice(-1))); return; }
      const wrong = typed; typed = ""; showTyped();
      fb.className = "fb warn";
      fb.textContent = `${NOT_YET} You're counting ${row.dir} by ${t}s${run.previous !== null ? ` from ${run.previous}` : ""}. ${missHint(run.target!, wrong.padEnd(target.length, "0"), run.previous, fwd ? "forward" : "back", t) || "Follow the arrow to the next key."}`;
    };
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable)) return; // typing elsewhere, e.g. the answer box
      if (run.done || e.metaKey || e.ctrlKey) return;
      if (e.key === "Backspace") { e.preventDefault(); typed = typed.slice(0, -1); showTyped(); fb.textContent = ""; fb.className = "fb"; return; }
      if (e.key === "Enter") { e.preventDefault(); if (typed) judge(); return; }
      if (!/^[0-9]$/.test(e.key)) return;
      e.preventDefault();
      const target = String(run.target!);
      typed += e.key; showTyped();
      if (typed.length >= target.length) { judge(); return; }
      if (target.startsWith(typed)) { fb.className = "fb good"; fb.textContent = KEEP_GOING; }
      else { fb.textContent = ""; fb.className = "fb"; }
    };
    document.addEventListener("keydown", onKey);
    warm!.cleanup = () => document.removeEventListener("keydown", onKey);
    btnFor(row.targets[0]! % 10)?.classList.add("cur");
    btnFor(row.targets[0]! % 10)?.focus({ preventScroll: true });
  }
  /** Review tables: type each number. Keypad shown (start circled) only at review Stage 2. */
  function drawTypedCount(row: CountRow) {
    const box = $("warm"), run = new CountRun(row), t = row.table, fwd = row.dir === "forward";
    // Review scaffold (Meliesa, Oct 29): the anchors stay visible and the key being counted is outlined. No arrows — the student types.
    const anchors = ROUTES[t] ? [1, 5, 10].map((k) => ({ key: keyForFact(t, k)!, value: t * k })) : [];
    const activeKey = (i: number) => (ROUTES[t] ? keyForFact(t, row.targets[i]! / t) ?? undefined : undefined);
    const kpGuide = (i: number): Guide => ({ anchors, active: activeKey(i) });
    const kpInner = (i: number) => `<div id="wkp">${keypadHTML(t, "static", kpGuide(i))}</div><p class="kpnote">Gold: the anchors. The outlined key is the one you're on.</p>`;
    // Stage 2 review: keypad on screen. Later reviews: the keypad is there if you need it — one click, and it follows along as you type.
    const kp = !ROUTES[t] ? "" : row.stage <= 2 ? `<div class="toolcol"><span class="lbl">Keypad</span>${kpInner(0)}</div>`
      : `<div class="toolcol" id="wtool"><span class="lbl">Tools</span><button type="button" class="btn ghost small" id="wShowKp">Show the keypad</button></div>`;
    box.innerHTML = `${warmHeader(`Review the ${t}s before today's lesson. Say each number out loud as you type it.`)}
      <h3>Review: count ${row.dir} by ${t}s</h3>${stepLine()}
      <div class="work"><div><div class="cells" id="wcells"></div><div class="fb" id="wfb"></div></div>${kp}</div>`;
    const c = $("wcells"), fb = $("wfb");
    const twice = countedTwice(t);
    if (twice) c.classList.add("twice");
    let cur = 0;
    const moveActive = (i: number) => { cur = i; const k = box.querySelector<HTMLElement>("#wkp"); if (k) k.innerHTML = keypadHTML(t, "static", kpGuide(i)); };
    const showBtn = box.querySelector<HTMLButtonElement>("#wShowKp");
    if (showBtn) showBtn.onclick = () => {
      const tool = box.querySelector<HTMLElement>("#wtool")!;
      tool.innerHTML = `<span class="lbl">Keypad</span>${kpInner(cur)}`;
      (c.querySelector("input:not([disabled]):not([readonly])") as HTMLInputElement | null)?.focus({ preventScroll: true });
    };
    const first = document.createElement("span"); first.className = "cell ok"; first.textContent = fwd ? "0" : String(t * SETTINGS.COUNT_TO + t); // lead-in
    if (fwd && !twice) c.appendChild(first);
    row.targets.forEach((v, i) => {
      const inp = document.createElement("input"); inp.className = "cell"; inp.inputMode = "numeric"; inp.autocomplete = "off";
      inp.setAttribute("aria-label", `${row.dir} by ${t}s, number ${i + 1}`);
      inp.disabled = i !== 0;
      const check = (final: boolean) => {
        const val = inp.value.trim(), exp = String(v);
        if (val === exp) {
          run.enter(v);
          inp.classList.remove("bad"); inp.classList.add("ok"); inp.readOnly = true; fb.textContent = ""; fb.className = "fb";
          const nxt = c.querySelectorAll("input")[i + 1] as HTMLInputElement | undefined;
          if (nxt) { moveActive(i + 1); nxt.disabled = false; nxt.focus(); } else rowDone();
        } else if (final || val.length >= exp.length) {
          inp.classList.add("bad"); fb.className = "fb warn";
          fb.textContent = `${NOT_YET} You're counting ${row.dir} by ${t}s${run.previous !== null ? ` from ${run.previous}` : ""}. ${missHint(v, val, run.previous, fwd ? "forward" : "back", t)}`.trim();
          inp.select();
        } else if (val !== "" && exp.startsWith(val)) { inp.classList.remove("bad"); fb.className = "fb good"; fb.textContent = KEEP_GOING; }
        else inp.classList.remove("bad");
      };
      inp.addEventListener("input", () => check(false));
      inp.addEventListener("keydown", (e) => { if (e.key === "Enter" && inp.value.trim() !== "") { e.preventDefault(); check(true); } });
      c.appendChild(inp);
    });
    (c.querySelector("input") as HTMLInputElement).focus({ preventScroll: true });
  }
  function drawWarmFact(row: FactRow) {
    const { a, b } = row, st = row.stageDef ?? PATTERN_STAGES[row.stage]!, box = $("warm");
    box.innerHTML = `${warmHeader(row.stageDef ? st.desc : `Review the ${a}s: ${st.name.toLowerCase()}.`)}
      <h3>${row.stageDef ? "Times facts review" : `Review: the ${a}s`}</h3>${stepLine()}<div id="wprob"></div>`;
    mountProblem($("wprob"), a, b, st, { recycled: false, index: null, onDone: (ok, tries, ms, used) => {
      recordAttempt(P, { a, b, correct: ok, firstTry: tries === 1, timed: !!st.timed, usedTool: used, ms });
      void store.logAttempt({ studentId: student.id, unitId: unit().id, stage: -1 - row.stage, a, b, correct: ok, firstTry: tries === 1, usedTool: used, ms });
      save(); renderMap();
      setTimeout(rowDone, tries > 1 ? 1200 : 600);
    }, onMiss: () => { save(); renderMap(); } });
  }
  function rowDone() {
    if (!warm) return;
    warm.cleanup?.(); warm.cleanup = null;
    warm.i++;
    if (warm.i < warm.rows.length) { setTimeout(drawWarmRow, 350); return; }
    markWarmDone(P, unit().id); save(); warm = null;
    renderWarm(); renderUnits(); renderStages(); startSheet();
  }

  /* ---------- Build your keypad (shared by the warm-up review and Stage 3) ---------- */
  function drawBuild(box: HTMLElement, t: number, ctx: { review: boolean; stage: number }, onBuilt: () => void, head: string) {
    const order = buildOrder(t), even = t % 2 === 0;
    box.innerHTML = head + `<div class="work"><div>
        <p style="margin-top:0"><strong>Draw your keypad for the ${t}s on your paper.</strong> Then build it here: start with <b>${order[0]}</b> and write the ${even ? "even numbers" : "numbers"} in order, ${even ? "2 to 8" : "1 to 9"}. Don't forget the zero at the bottom.${even ? " The odd spaces stay blank." : ""}</p>
        <div class="row"><button class="btn" id="buildCheck" type="button">Check my keypad</button></div><div class="fb" id="bfb"></div></div>
      <div class="toolcol"><span class="lbl">Your keypad</span><div id="buildBox">${keypadHTML(t, "build")}</div></div></div>`;
    const inputs = [...box.querySelectorAll<HTMLInputElement>("#buildBox input")];
    const byDigit = (d: number) => inputs.find((i) => Number(i.dataset.d) === d)!;
    byDigit(order[0]!).focus({ preventScroll: true });
    // Typing a digit moves on to the next space in writing order.
    inputs.forEach((inp) => {
      inp.addEventListener("input", () => {
        if (inp.value.trim() === "") return;
        const d = Number(inp.dataset.d), k = order.indexOf(d);
        if (k >= 0 && k < order.length - 1) byDigit(order[k + 1]!).focus({ preventScroll: true });
      });
      inp.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); (box.querySelector("#buildCheck") as HTMLButtonElement).click(); } });
    });
    (box.querySelector("#buildCheck") as HTMLButtonElement).onclick = () => {
      let wrong = 0, filledOdd = false;
      for (const inp of inputs) {
        const d = Number(inp.dataset.d), exp = keyShows(t, d) ? String(d) : "", val = inp.value.trim(), ok = val === exp;
        inp.classList.toggle("bad", !ok); inp.classList.toggle("ok", ok && exp !== "");
        if (!ok) wrong++;
        if (!ok && exp === "" && val !== "") filledOdd = true;
      }
      const f = box.querySelector("#bfb") as HTMLElement;
      if (!wrong) { f.className = "fb good"; f.textContent = "That's your keypad."; setTimeout(onBuilt, 900); }
      else { f.className = "fb warn"; f.textContent = `${NOT_YET} Check the spaces outlined in orange. ${even && filledOdd ? "On an even table, the odd spaces stay blank." : "Each key is its own number, in order."}`; }
    };
    void ctx;
  }

  /* ---------- Stages ---------- */
  function renderStages() {
    const box = $("stages"), u = unit();
    if (view.mode === "review") { box.innerHTML = ""; box.hidden = true; return; }
    box.hidden = false; box.innerHTML = "";
    const us = unitRecord(P, u.id);
    stagesFor(u).forEach((st, i) => {
      const b = document.createElement("button");
      b.type = "button"; b.className = "stage" + (us.stage > i ? " passed" : "");
      b.setAttribute("aria-pressed", String(view.mode === "unit" && view.stage === i));
      b.disabled = i > us.stage && !o.preview;
      b.innerHTML = `<b>${i + 1}</b> ${esc(st.name)}${b.disabled ? " 🔒" : ""}`;
      b.onclick = () => { view = { mode: "unit", stage: i }; renderStages(); startSheet(); };
      box.appendChild(b);
    });
  }

  /* ---------- Worksheet ---------- */
  function startSheet() {
    sheet = view.mode === "review" ? buildReview(unit().id, P) : buildWorksheet(unit(), P);
    sheet.built = !stage().build;
    renderPractice();
  }
  function renderPractice() {
    const u = unit(), st = stage(), box = $("practice");
    $("practiceTitle").textContent = view.mode === "review" ? "Mixed review" : "Practice";
    if (view.mode === "unit" && !warmDone(u)) {
      box.innerHTML = `<div class="locked"><strong>Warm-up first.</strong><br>The problems open when step 2 is done.</div>`;
      return;
    }
    if (!sheet) startSheet();
    const ws = sheet!;
    const head = `<div class="stagehead"><h3>${view.mode === "review" ? "" : "Stage " + (view.stage + 1) + ": "}${esc(st.name)}</h3><p>${esc(st.desc)}</p></div>`;
    if (!ws.built) return drawBuild(box, u.tables[0]!, { review: false, stage: view.mode === "unit" ? view.stage : 9 }, () => { ws.built = true; renderPractice(); }, head);
    if (ws.i >= ws.items.length) return renderEnd(head);
    const [a, b] = ws.items[ws.i]!;
    const recycled = isRecycled(ws);
    box.innerHTML = head + `<div id="prob"></div>`;
    const div = view.mode === "unit" && isDiv(u);
    mountProblem($("prob"), a, b, st, { recycled, index: recycled ? null : ws.i, orig: ws.orig, div, onDone: (ok, tries, ms, used) => {
      const fluent = recordAttempt(P, { a, b, correct: ok, firstTry: tries === 1, timed: !!st.timed, usedTool: used, ms, op: div ? "div" : "mul" });
      if (tries === 1 && !recycled) ws.firstTry++;
      if (fluent) ws.fluent++;
      // Headed for mastery: a fact that took longer than the goal comes back at the end of the sheet, quietly (Meliesa, Oct 29).
      if (st.timed && ok && tries === 1 && !fluent && !recycled) recycle(ws, a, b);
      // Division attempts log with stage + 20 (the attempts table has no op column); the teacher view decodes it.
      void store.logAttempt({ studentId: student.id, unitId: unit().id, stage: view.mode === "review" ? 9 : view.stage + (div ? 20 : 0), a, b, correct: ok, firstTry: tries === 1, usedTool: used, ms });
      save(); renderMap();
      setTimeout(() => { ws.i++; renderPractice(); }, tries > 1 ? 1500 : 850);
    }, onMiss: (tries) => { save(); renderMap(); if (tries >= 2 && !recycled) recycle(ws, a, b); } });
  }

  /** One problem with the stage's tools. Shared by the worksheet and the warm-up review. */
  function mountProblem(box: HTMLElement, a: number, b: number, st: Stage, h: {
    recycled: boolean; index: number | null; orig?: number;
    /** Division: the problem is (a×b) ÷ a and the answer is b — how many times you counted. */
    div?: boolean;
    onDone: (ok: boolean, tries: number, ms: number, usedTool: boolean) => void;
    onMiss: (tries: number) => void;
  }) {
    const div = !!h.div, answer = div ? b : a * b;
    const p = puzzleFor(a, b);
    const anchors = ROUTES[a] ? [1, 5, 10].map((k) => ({ key: keyForFact(a, k)!, value: a * k })) : [];
    const cueText = () => (div ? divCue(a, b) : anchorCue(a, b));
    let tool = "";
    // Division Stage 1: the number's key is outlined, the anchors are lit, and every touch is one count.
    const guide: Guide | undefined = div && st.kp && !st.anchorCue ? { anchors, active: keyForFact(a, b) ?? undefined } : div && st.kp ? { anchors, puzzle: p } : guideForStage(st, a, b);
    const note = !ROUTES[a] || !st.guide ? "" : div && !st.anchorCue ? `<p class="kpnote">Start at ${routeStart(a)} and count by ${a}s to ${a * b}. Every touch is one count: <b id="touches">0 touches</b>.</p>` : `<p class="kpnote">${p.caption ? `${p.caption}.` : st.guide === "full" ? `Start at ${routeStart(a)}. Follow the arrows, then 0.` : ""}</p>`;
    if (st.kp && ROUTES[a]) tool = `<div class="toolcol"><span class="lbl">Keypad</span><div id="kpBox">${keypadHTML(a, "live", guide)}${note}<div class="row" style="justify-content:center;margin-top:8px"><button type="button" class="btn ghost small" data-clear>Clear marks</button></div></div></div>`;
    else if (st.tools) tool = `<div class="toolcol"><span class="lbl">Tools</span><div class="row"><button type="button" class="btn ghost small" id="useKeypad">Show the keypad</button><button type="button" class="btn ghost small" id="useAnchor">Show the anchor</button></div><div id="kpBox"></div></div>`;
    const eleventh = !div && b === 12 && a <= 9 ? `<div class="eleventh"><span class="lbl">The 11th</span><span class="q" style="font-size:1.8rem">${a} × 11 = ${a * 11}</span></div>` : "";
    const q = div ? `${a * b} ÷ ${a} =` : `${a} × ${b} =`;
    box.innerHTML = `<div class="work"><div>
        <div class="pmeta">${h.recycled ? "Back again: a fact to revisit" : h.index !== null ? `Problem ${h.index + 1} of ${h.orig}` : ""}</div>
        ${eleventh}
        <div class="bigq"><span class="q">${q}</span><input id="ans" inputmode="numeric" autocomplete="off" aria-label="${div ? `${a * b} divided by ${a}` : `${a} times ${b}`}"></div>
        <div class="row" style="margin-top:6px"><button class="btn" id="checkBtn" type="button">Check</button></div>
        <div id="cue">${st.anchorCue ? `<div class="cue">${rich(cueText())}</div>` : ""}</div>
        <div class="fb" id="fb"></div></div>${tool}</div>`;
    const inp = box.querySelector<HTMLInputElement>("#ans")!;
    const cueBox = box.querySelector<HTMLElement>("#cue")!, fbEl = box.querySelector<HTMLElement>("#fb")!;
    if (st.kp && ROUTES[a]) {
      if (div && !st.anchorCue) wireCounting(box.querySelector("#kpBox")!, (n) => { const t = box.querySelector("#touches"); if (t) t.textContent = `${n} touch${n === 1 ? "" : "es"}`; inp.focus({ preventScroll: true }); });
      else wireMarking(box.querySelector("#kpBox")!, () => inp.focus({ preventScroll: true }));
    }
    let used = false;
    const showKeypad = () => {
      used = true;
      const kb = box.querySelector<HTMLElement>("#kpBox");
      if (kb && ROUTES[a] && !kb.innerHTML) kb.innerHTML = keypadHTML(a, "static", { anchors }) + `<p class="kpnote">Gold: the anchors.</p>`;
      const btn = box.querySelector<HTMLButtonElement>("#useKeypad"); if (btn) btn.disabled = true;
      inp.focus();
    };
    const showAnchor = () => {
      used = true;
      cueBox.innerHTML = `<div class="cue">${rich(cueText())}</div>`;
      const kb = box.querySelector<HTMLElement>("#kpBox");
      if (kb && ROUTES[a]) kb.innerHTML = keypadHTML(a, "static", { anchors, puzzle: p }) + (p.caption && !div ? `<p class="kpnote">${p.caption}.</p>` : "");
      box.querySelectorAll<HTMLButtonElement>("#useAnchor,#useKeypad").forEach((btn) => (btn.disabled = true));
      inp.focus();
    };
    const ua = box.querySelector<HTMLButtonElement>("#useAnchor"); if (ua) ua.onclick = showAnchor;
    const uk = box.querySelector<HTMLButtonElement>("#useKeypad"); if (uk) uk.onclick = showKeypad;
    inp.focus({ preventScroll: true });
    const started = performance.now();
    let tries = 0, done = false;
    const say = (t: string, c: string) => { fbEl.textContent = t; fbEl.className = "fb " + c; };
    const check = () => {
      if (done) return;
      const v = inp.value.trim();
      if (v === "") return;
      const ms = performance.now() - started, ok = Number(v) === answer;
      tries++;
      if (ok) {
        done = true; inp.readOnly = true;
        const fluent = st.timed && tries === 1 && !used && ms < SETTINGS.FLUENT_MS;
        // Times are kept internal (Meliesa, Oct 29): no seconds on screen, nothing to be slow at. The goal is stated once, at the stage.
        void fluent;
        say(tries > 1 ? `That's it: ${answer}.${h.recycled || h.index === null ? "" : " You'll see this one again at the end."}` : CORRECT, "good");
        h.onDone(true, tries, ms, used);
      } else {
        recordAttempt(P, { a, b, correct: false, firstTry: tries === 1, timed: !!st.timed, usedTool: used, ms, op: div ? "div" : "mul" });
        h.onMiss(tries);
        if (tries === 1) {
          let hint: string;
          if (div) {
            if (Number(v) === a * b) hint = `${NOT_YET} ${LANDED_ON}`;
            else if (st.timed) hint = NOT_YET;
            else if (st.tools) { hint = `${NOT_YET} Count by ${a}s to ${a * b} — the answer is how many times you counted. The keypad and the anchor are a click away.`; }
            else hint = `${NOT_YET} Count by ${a}s to ${a * b}. The answer is how many times you counted — not the number you landed on.`;
            say(hint, "warn");
          } else {
            if (st.kp && !st.anchorCue) hint = `${NOT_YET} Start at the anchor and follow the arrows.`;
            else if (st.anchorCue) hint = `${NOT_YET} Start at the anchor and count again.`;
            else if (st.tools) hint = `${NOT_YET} You can show the keypad or the anchor if you need them.`;
            else { cueBox.innerHTML = `<div class="cue">${rich(anchorCue(a, b))}</div>`; hint = `${NOT_YET} Here's a place to start.`; }
            say(`${hint} ${st.timed ? "" : missHint(a * b, v, a * (b - 1))}`.trim(), "warn");
          }
        } else {
          say(`${q} ${answer}. Type it in to keep going.`, "warn");
        }
        inp.select();
      }
    };
    box.querySelector<HTMLButtonElement>("#checkBtn")!.onclick = check;
    inp.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); check(); } });
    if (!st.timed && !div) inp.addEventListener("input", () => {
      if (done) return;
      const v = inp.value.trim(), exp = String(a * b);
      if (v !== "" && v.length < exp.length && exp.startsWith(v)) say(KEEP_GOING, "good");
      else if (fbEl.textContent === KEEP_GOING) say("", "");
    });
  }

  function renderEnd(head: string) {
    const u = unit(), st = stage(), box = $("practice"), stages = stagesFor(u), ws = sheet!;
    let msg = "", btns = "";
    if (view.mode === "review") {
      msg = "Review done. Missed facts stay on the revisit list.";
      btns = `<button class="btn" id="again2" type="button">Another review</button><button class="btn ghost" id="backUnit" type="button">Back to ${esc(u.name)}</button>`;
    } else if (passed(ws)) {
      const complete = passStage(P, u.id, view.stage); save();
      const ni = UNITS.indexOf(u) + 1;
      const nextOpen = unitRecord(P, u.id).stage >= STAGES_TO_UNLOCK_NEXT && ni < UNITS.length;
      if (!complete) {
        msg = `Stage ${view.stage + 1} passed.${nextOpen ? ` ${UNITS[ni]!.name} is open.${isDiv(u) ? "" : ` The ${u.tables[0]}s come back in every warm-up from here, with a little less help each time.`}` : ""}`;
        btns = (nextOpen ? `<button class="btn" id="nextUnit" type="button">Start ${esc(UNITS[ni]!.name)}</button>` : "") +
          `<button class="btn ghost" id="nextStage" type="button">Keep going: Stage ${view.stage + 2}</button><button class="btn ghost" id="again2" type="button">Practice this stage again</button>`;
      } else {
        msg = `${u.name} complete, with no tools.`;
        btns = (ni < UNITS.length ? `<button class="btn" id="nextUnit" type="button">Start ${esc(UNITS[ni]!.name)}</button>` : "") + `<button class="btn ghost" id="again2" type="button">Practice again</button>`;
      }
    } else {
      msg = `Let's do this stage again. The goal is ${SETTINGS.PASS} of ${ws.orig} correct on the first try.`;
      btns = `<button class="btn" id="again2" type="button">New worksheet</button>`;
    }
    box.innerHTML = head + `<div class="summary">
        <div class="stat"><b>${ws.firstTry} / ${ws.orig}</b><span>correct on the first try</span></div>
        <div class="stat"><b>${st.timed ? ws.orig - ws.fluent : "—"}</b><span>${st.timed ? "to review again" : "review starts in the No tools stage"}</span></div>
        <div class="stat"><b>${revisitIn(P, UNITS.flatMap((x) => x.tables)).length}</b><span>facts to revisit</span></div>
      </div><p class="fb good">${msg}</p><div class="row">${btns}</div>`;
    renderUnits(); renderStages();
    const on = (id: string, fn: () => void) => { const el = root.querySelector<HTMLElement>(`#${id}`); if (el) el.onclick = fn; };
    on("again2", startSheet);
    on("nextStage", () => { if (view.mode === "unit") view.stage++; renderStages(); startSheet(); });
    on("nextUnit", () => { P.currentUnit = UNITS[UNITS.indexOf(u) + 1]!.id; view = { mode: "unit", stage: 0 }; save(); build(); window.scrollTo({ top: 0 }); });
    on("backUnit", () => { view = { mode: "unit", stage: Math.min(unitRecord(P, u.id).stage, stages.length - 1) }; build(); });
  }

  /* ---------- Fact map ---------- */
  function renderMap() {
    $("map").innerHTML = factMapHTML(P);
    const show = hasDivision(P);
    $("divmapwrap").hidden = !show;
    if (show) $("divmap").innerHTML = factMapHTML(P, "div");
  }

  /* ---------- Page ---------- */
  function build() {
    const u = unit(), review = view.mode === "review";
    renderUnits();
    $("warmStep").hidden = review;
    $("eyebrow").textContent = review ? "Mixed review" : `Tigard Times · Unit ${UNITS.indexOf(u) + 1} of ${UNITS.length}`;
    $("title").textContent = review ? "Every table so far" : u.name;
    warm?.cleanup?.(); warm = null; sheet = null;
    if (!review) renderWarm();
    renderStages(); startSheet(); renderMap();
  }
  $("review").onclick = () => { view = { mode: "review" }; build(); $("practice").scrollIntoView({ block: "start" }); };
  $("back").onclick = () => { warm?.cleanup?.(); o.onBack(); };
  build();
  return () => warm?.cleanup?.();
}
